/**
 * SESSION 146au — SmartFlow Tab (Phase 1: Data Inspector — REDESIGNED)
 *
 * UX feedback addressed:
 *   - Confusion about "90 day limit" → backfill is unbounded, presets are
 *     just shortcuts. Made explicit with "Custom range" inputs.
 *   - "Nothing happened on Run Backfill" → live progress strip with
 *     polling every 3s + a clear "Backfill in progress" banner.
 *   - "Load button" was confusing → renamed to "Search" and merged into
 *     a single tabbed Inspector (By Date / By Symbol).
 *   - "Do we have to do it everyday?" → NO. Auto-runs at 18:30 IST daily.
 *     Surface this prominently at the top.
 *   - 100% delivery ETFs polluted the view → "Real stocks only" toggle
 *     filters ETFs/liquid funds (default ON).
 */

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Activity, Loader2, Play, RefreshCw, Database, Calendar,
  TrendingUp, BarChart3, Search, CheckCircle2, AlertCircle, Clock,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import MLPanel from './SmartFlowMLPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const formatINR = (n) => {
  if (n == null) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};
const fmtNum = (n, dp = 2) =>
  typeof n === 'number' ? n.toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : '—';

const Pill = ({ tone = 'neutral', children, testId }) => {
  const palette = {
    success: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    warn: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    danger: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
    info: 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
    neutral: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30',
    accent: 'bg-violet-500/15 text-violet-300 ring-violet-500/30',
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${palette[tone] || palette.neutral}`}>
      {children}
    </span>
  );
};

const todayISO = () => new Date(Date.now() + 330 * 60000).toISOString().split('T')[0];
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

// =====================================================================
// BACKTEST PANEL — Phase 3 walk-forward edge validator
// =====================================================================

const BacktestPanel = ({ getAuth }) => {
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [periodFrom, setPeriodFrom] = useState(daysAgoISO(365));
  const [periodTo, setPeriodTo] = useState(daysAgoISO(7));
  const [holdingDays, setHoldingDays] = useState(5);
  const [topN, setTopN] = useState(5);
  const [scoreThreshold, setScoreThreshold] = useState(70);
  const [universeFilter, setUniverseFilter] = useState('nifty200');
  const [sampleEvery, setSampleEvery] = useState(5);
  // 147b — exit + regime toggles (defaults: SL off, trail off, regime ON — opt in)
  const [useStopLoss, setUseStopLoss] = useState(false);
  const [stopLossAtrMult, setStopLossAtrMult] = useState(2.0);
  const [useAtrTrail, setUseAtrTrail] = useState(false);
  const [trailAfterPct, setTrailAfterPct] = useState(5);
  const [useRegimeFilter, setUseRegimeFilter] = useState(true);
  // 147c — eligibility gate sliders (research-driven). Defaults match production rule.
  const [minDeliveryPct, setMinDeliveryPct] = useState(50);
  const [minDeliveryZ, setMinDeliveryZ] = useState(1.0);
  const [requireAboveEma20, setRequireAboveEma20] = useState(true);
  // 147d — ML overlay (off by default; user must train a model first)
  const [useMlOverlay, setUseMlOverlay] = useState(false);
  const [mlThreshold, setMlThreshold] = useState(0.4);
  const [showTrades, setShowTrades] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/api/admin/smartflow/backtest/latest`, { headers: getAuth() });
      setLatest(r.data?.result || null);
      setRunning(!!r.data?.is_running);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [getAuth]);

  useEffect(() => { refresh(); }, [refresh]);

  const trigger = async () => {
    try {
      setRunning(true);
      const r = await axios.post(`${API}/api/admin/smartflow/backtest/run`, {
        period_from: periodFrom,
        period_to: periodTo,
        holding_days: Number(holdingDays),
        top_n_per_day: Number(topN),
        score_threshold: Number(scoreThreshold),
        universe_filter: universeFilter,
        sample_every: Number(sampleEvery),
        use_stop_loss: useStopLoss,
        stop_loss_atr_mult: Number(stopLossAtrMult),
        use_atr_trail: useAtrTrail,
        trail_after_pct: Number(trailAfterPct),
        use_regime_filter: useRegimeFilter,
        min_delivery_pct: Number(minDeliveryPct),
        min_delivery_z: Number(minDeliveryZ),
        require_above_ema20: requireAboveEma20,
        use_ml_overlay: useMlOverlay,
        ml_threshold: Number(mlThreshold),
      }, { headers: getAuth() });
      toast.success(r.data?.message || 'Backtest started.');
      let polled = 0;
      const id = setInterval(async () => {
        polled++;
        try {
          const lr = await axios.get(`${API}/api/admin/smartflow/backtest/latest`, { headers: getAuth() });
          setLatest(lr.data?.result || null);
          setRunning(!!lr.data?.is_running);
          if (!lr.data?.is_running || polled > 80) clearInterval(id);
        } catch { /* ignore */ }
      }, 15000);
    } catch (e) {
      toast.error(`Trigger: ${e?.response?.data?.detail || e.message}`);
      setRunning(false);
    }
  };

  return (
    <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/15 p-4 space-y-3" data-testid="smartflow-backtest-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <BarChart3 size={14} className="text-emerald-300" /> Walk-Forward Backtest
            <Pill tone="success">Phase 3</Pill>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed max-w-2xl">
            Validates edge: for every Nth trading day, scores universe → picks top-N → buys next-day open → holds 5 days → exits at close.
            No look-ahead. Tells you if the score is real before committing to ML.
          </p>
        </div>
        <button onClick={refresh} disabled={loading} className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50" data-testid="smartflow-bt-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          From
          <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-from" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          To
          <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-to" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Holding (days)
          <input type="number" min={1} max={30} value={holdingDays} onChange={(e) => setHoldingDays(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-hold" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Top N / day
          <input type="number" min={1} max={25} value={topN} onChange={(e) => setTopN(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-topn" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Score ≥
          <input type="number" min={0} max={100} value={scoreThreshold} onChange={(e) => setScoreThreshold(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-score" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Universe
          <select value={universeFilter} onChange={(e) => setUniverseFilter(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-universe">
            <option value="nifty200">Nifty 200 (recommended)</option>
            <option value="all">All real stocks</option>
          </select>
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1" title="Higher = faster but coarser. 1 = score every day, 5 = score every 5th day.">
          Sample every
          <select value={sampleEvery} onChange={(e) => setSampleEvery(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-sample">
            <option value={1}>1 day (slow, full)</option>
            <option value={3}>3 days</option>
            <option value={5}>5 days (recommended)</option>
            <option value={10}>10 days (fast)</option>
          </select>
        </label>
        <button onClick={trigger} disabled={running} className="rounded-md bg-emerald-600 hover:bg-emerald-500 px-3 py-2 text-xs text-white font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="smartflow-bt-run">
          {running ? <Loader2 className="animate-spin" size={12} /> : <Play size={12} />}
          {running ? 'Running…' : 'Run Backtest'}
        </button>
      </div>

      {/* 147b — Exit-rule + regime toggles row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 items-end pt-1 border-t border-emerald-700/20">
        <label className="text-[11px] text-zinc-400 flex items-center gap-2 col-span-2 md:col-span-1" title="Hard stop at entry − N × ATR(14)">
          <input type="checkbox" checked={useStopLoss} onChange={(e) => setUseStopLoss(e.target.checked)} className="accent-emerald-500" data-testid="smartflow-bt-toggle-sl" />
          <span>Stop loss (× ATR)</span>
          <input type="number" min={0.5} max={5} step={0.1} value={stopLossAtrMult} onChange={(e) => setStopLossAtrMult(e.target.value)} disabled={!useStopLoss} className="w-14 bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-zinc-200 text-xs disabled:opacity-40" data-testid="smartflow-bt-sl-mult" />
        </label>
        <label className="text-[11px] text-zinc-400 flex items-center gap-2" title="ATR trail activates once this profit % is reached, then stops at high − 1×ATR">
          <input type="checkbox" checked={useAtrTrail} onChange={(e) => setUseAtrTrail(e.target.checked)} className="accent-emerald-500" data-testid="smartflow-bt-toggle-trail" />
          <span>ATR trail after</span>
          <input type="number" min={0.5} max={20} step={0.5} value={trailAfterPct} onChange={(e) => setTrailAfterPct(e.target.value)} disabled={!useAtrTrail} className="w-14 bg-zinc-900 border border-zinc-700 rounded px-1 py-0.5 text-zinc-200 text-xs disabled:opacity-40" data-testid="smartflow-bt-trail-pct" />
          <span>%</span>
        </label>
        <label className="text-[11px] text-zinc-400 flex items-center gap-2 col-span-2 md:col-span-1" title="Skip new entries when NIFTY 50 closes below its 200-day MA">
          <input type="checkbox" checked={useRegimeFilter} onChange={(e) => setUseRegimeFilter(e.target.checked)} className="accent-emerald-500" data-testid="smartflow-bt-toggle-regime" />
          <span>NIFTY &gt; 200DMA gate</span>
        </label>
        <div className="text-[10px] text-zinc-500 italic md:col-span-2">
          147b: per-bar exits + portfolio-aware DD (capital split across top-N slots).
        </div>
      </div>

      {/* 147c — Eligibility gate overrides (research-driven) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 items-end pt-1 border-t border-emerald-700/20">
        <label className="text-[11px] text-zinc-400 flex flex-col gap-1" title="Min delivery % on entry day. Default 50. Research finding: winners avg 51% vs losers 54%, so try lowering to 30 or even 0 to test mean-reversion bounces.">
          Min delivery %
          <input type="number" min={0} max={95} step={5} value={minDeliveryPct} onChange={(e) => setMinDeliveryPct(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-min-deliv" />
        </label>
        <label className="text-[11px] text-zinc-400 flex flex-col gap-1" title="Min delivery z-score vs 90-day. Mean-relative; safe to keep ≥1.">
          Min delivery z
          <input type="number" min={-3} max={5} step={0.25} value={minDeliveryZ} onChange={(e) => setMinDeliveryZ(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-bt-min-dz" />
        </label>
        <label className="text-[11px] text-zinc-400 flex items-center gap-2" title="Require close > EMA20. Turn OFF to capture mean-reversion bounces from 52w-low region (per Phase A research finding).">
          <input type="checkbox" checked={requireAboveEma20} onChange={(e) => setRequireAboveEma20(e.target.checked)} className="accent-emerald-500" data-testid="smartflow-bt-toggle-ema20" />
          <span>Require close &gt; EMA20</span>
        </label>
        <div className="text-[10px] text-zinc-500 italic md:col-span-2">
          147c: gate overrides — Phase A research showed delivery %% gate cuts winning region. Try min_delivery=30 + EMA20 OFF for "mean-reversion mode".
        </div>
      </div>

      {/* 147d — ML overlay */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 items-end pt-1 border-t border-violet-700/30">
        <label className="text-[11px] text-zinc-400 flex items-center gap-2 col-span-2 md:col-span-1" title="Apply trained LightGBM model as a quality filter on top of rule-score picks. Requires Phase 4 training to have completed at least once.">
          <input type="checkbox" checked={useMlOverlay} onChange={(e) => setUseMlOverlay(e.target.checked)} className="accent-violet-500" data-testid="smartflow-bt-toggle-ml" />
          <span>ML overlay (Phase 4 model)</span>
        </label>
        <label className="text-[11px] text-zinc-400 flex flex-col gap-1" title="Min ML p_up to keep a pick. 0.40 = require at least 40% predicted probability of hitting the target.">
          ML threshold p_up ≥
          <input type="number" min={0} max={1} step={0.05} value={mlThreshold} onChange={(e) => setMlThreshold(e.target.value)} disabled={!useMlOverlay} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs disabled:opacity-40" data-testid="smartflow-bt-ml-threshold" />
        </label>
        <div className="text-[10px] text-violet-300/80 italic md:col-span-3">
          147d: trained model must exist (run "Train Model" in the violet panel above first). Result card will show p_up per trade + winners-vs-losers avg.
        </div>
      </div>

      {latest ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="smartflow-bt-result">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] text-zinc-500 font-mono">{latest.backtest_id}</div>
              <div className="text-xs text-zinc-300 mt-0.5">
                {latest.period_from} → {latest.period_to} · hold {latest.holding_days}d · top{latest.top_n_per_day} · score≥{latest.score_threshold} · universe={latest.universe_filter}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                {latest.use_stop_loss ? `SL ${latest.stop_loss_atr_mult}×ATR` : 'no SL'}
                {' · '}
                {latest.use_atr_trail ? `trail >${latest.trail_after_pct}%` : 'no trail'}
                {' · '}
                {latest.use_regime_filter ? `regime ON (${latest.skipped_regime || 0} days skipped)` : 'regime OFF'}
                {latest.avg_bars_held != null && ` · avg hold ${latest.avg_bars_held}d`}
                {latest.use_ml_overlay && (
                  <span className="text-violet-300">
                    {' · '}ML overlay ON @ p_up≥{latest.ml_threshold} · {latest.ml_skipped || 0} skipped
                    {latest.avg_p_up_winners > 0 && ` · winners μ=${latest.avg_p_up_winners}`}
                    {latest.avg_p_up_losers > 0 && ` · losers μ=${latest.avg_p_up_losers}`}
                  </span>
                )}
              </div>
            </div>
            <Pill tone={latest.status === 'completed' ? 'success' : latest.status === 'running' ? 'info' : 'danger'}>{latest.status}</Pill>
          </div>
          {latest.status === 'completed' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                <SimpleStat label="Trades" value={latest.total_trades} testId="bt-stat-trades" />
                <SimpleStat label="Win Rate" value={`${latest.win_rate_pct?.toFixed(1)}%`} tone={latest.win_rate_pct >= 55 ? 'success' : 'warn'} testId="bt-stat-wr" />
                <SimpleStat label="Avg Win" value={`+${latest.avg_winner_pct?.toFixed(2)}%`} tone="success" testId="bt-stat-aw" />
                <SimpleStat label="Avg Loss" value={`${latest.avg_loser_pct?.toFixed(2)}%`} tone="danger" testId="bt-stat-al" />
                <SimpleStat label="Expectancy" value={`${latest.expectancy_pct >= 0 ? '+' : ''}${latest.expectancy_pct?.toFixed(2)}%`} tone={latest.expectancy_pct >= 1 ? 'success' : latest.expectancy_pct >= 0 ? 'warn' : 'danger'} testId="bt-stat-exp" />
                <SimpleStat label="Sharpe" value={latest.sharpe?.toFixed(2)} tone={latest.sharpe >= 1 ? 'success' : 'warn'} testId="bt-stat-sharpe" />
                <SimpleStat label="Max DD" value={`-${latest.max_drawdown_pct?.toFixed(2)}%`} tone={latest.max_drawdown_pct < 15 ? 'success' : 'warn'} testId="bt-stat-dd" />
              </div>

              {latest.exit_reason_breakdown && Object.keys(latest.exit_reason_breakdown).length > 0 && (
                <div className="text-[11px] text-zinc-400 border-t border-zinc-800 pt-2">
                  <span className="text-zinc-500 mr-2">Exits:</span>
                  {Object.entries(latest.exit_reason_breakdown).map(([k, v]) => {
                    const tint = k === 'stop_loss' ? 'text-rose-300' : k === 'trail_stop' ? 'text-amber-300' : 'text-emerald-300';
                    return (
                      <span key={k} className={`mr-3 font-mono ${tint}`}>
                        {k.replace(/_/g, ' ')}: {v}
                      </span>
                    );
                  })}
                </div>
              )}

              {latest.by_score_bucket && Object.keys(latest.by_score_bucket).length > 0 && (
                <div className="text-[11px] text-zinc-400 border-t border-zinc-800 pt-2">
                  <span className="text-zinc-500 mr-2">By score bucket:</span>
                  {Object.entries(latest.by_score_bucket).map(([k, v]) => (
                    <span key={k} className="mr-3 font-mono">
                      {k}: {v.count} trades · win {v.win_rate_pct}% · avg {v.avg_pnl_pct >= 0 ? '+' : ''}{v.avg_pnl_pct}%
                    </span>
                  ))}
                </div>
              )}

              {Array.isArray(latest.trades) && latest.trades.length > 0 && (
                <div>
                  <button onClick={() => setShowTrades(!showTrades)} className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1" data-testid="smartflow-bt-toggle-trades">
                    {showTrades ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Trades ({latest.trades.length})
                  </button>
                  {showTrades && (
                    <div className="mt-2 overflow-x-auto rounded-lg border border-zinc-800 max-h-96">
                      <table className="w-full text-[11px]" data-testid="smartflow-bt-trades">
                        <thead className="bg-zinc-900/80 text-zinc-400 uppercase tracking-wider sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Symbol</th>
                            <th className="px-3 py-2 text-left">Entry</th>
                            <th className="px-3 py-2 text-right">Buy</th>
                            <th className="px-3 py-2 text-left">Exit</th>
                            <th className="px-3 py-2 text-right">Sell</th>
                            <th className="px-3 py-2 text-right">PnL %</th>
                            <th className="px-3 py-2 text-right">Score</th>
                            <th className="px-3 py-2 text-right">Z</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...latest.trades].sort((a, b) => (b.pnl_pct ?? 0) - (a.pnl_pct ?? 0)).map((t, i) => (
                            <tr key={`${t.symbol}-${t.entry_date}-${i}`} className="border-b border-zinc-800/60">
                              <td className="px-3 py-1.5 font-mono font-semibold text-zinc-100">{t.symbol}</td>
                              <td className="px-3 py-1.5 text-zinc-400 font-mono">{t.entry_date}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{fmtNum(t.entry_price)}</td>
                              <td className="px-3 py-1.5 text-zinc-400 font-mono">{t.exit_date}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{fmtNum(t.exit_price)}</td>
                              <td className={`px-3 py-1.5 text-right font-mono font-semibold ${t.pnl_pct > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                                {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct?.toFixed(2)}%
                              </td>
                              <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{t.score?.toFixed(0)}</td>
                              <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{t.delivery_pct_z?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {latest.status === 'failed' && (
            <div className="text-rose-300 text-xs font-mono">{latest.error}</div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-zinc-700 px-4 py-6 text-center text-sm text-zinc-400">
          No backtests yet. Defaults: 1y · Nifty 200 · hold 5d · top5 · score≥70. Click <span className="text-emerald-300 font-semibold">Run Backtest</span> (~5-8 min).
        </div>
      )}
    </div>
  );
};

// =====================================================================
// SCORING PANEL — Phase 2 daily Smart Money picks
// =====================================================================

const ScoringPanel = ({ getAuth }) => {
  const [scoringStatus, setScoringStatus] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [eligibleOnly, setEligibleOnly] = useState(true);
  const [minScore, setMinScore] = useState(0);
  const [liquidityTier, setLiquidityTier] = useState('all');
  const [nifty200Only, setNifty200Only] = useState(false);
  const [openSym, setOpenSym] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: '50',
        eligible_only: String(eligibleOnly),
        min_score: String(minScore),
        liquidity_tier: liquidityTier,
        nifty200_only: String(nifty200Only),
      });
      const [s, c] = await Promise.all([
        axios.get(`${API}/api/admin/smartflow/score/status`, { headers: getAuth() }),
        axios.get(`${API}/api/admin/smartflow/score/candidates?${params}`, { headers: getAuth() }),
      ]);
      setScoringStatus(s.data || null);
      setCandidates(c.data?.candidates || []);
    } catch { /* silent on auth failure */ }
    finally { setLoading(false); }
  }, [getAuth, eligibleOnly, minScore, liquidityTier, nifty200Only]);

  useEffect(() => { refresh(); }, [refresh]);

  const triggerScoring = async () => {
    try {
      setRunning(true);
      const r = await axios.post(`${API}/api/admin/smartflow/score/run`, { top_n: 50 }, { headers: getAuth() });
      toast.success(r.data?.message || 'Scoring triggered.');
      // Poll status every 5s.
      let polled = 0;
      const id = setInterval(async () => {
        polled++;
        try {
          const sr = await axios.get(`${API}/api/admin/smartflow/score/status`, { headers: getAuth() });
          setScoringStatus(sr.data);
          if (!sr.data?.scoring_in_progress || polled > 60) {
            clearInterval(id);
            refresh();
            setRunning(false);
          }
        } catch { /* ignore */ }
      }, 5000);
    } catch (e) {
      toast.error(`Trigger: ${e?.response?.data?.detail || e.message}`);
      setRunning(false);
    }
  };

  const lastRun = scoringStatus?.last_run;
  const inProgress = scoringStatus?.scoring_in_progress;

  return (
    <div className="rounded-xl border border-violet-700/30 bg-violet-950/15 p-4 space-y-3" data-testid="smartflow-scoring-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <BarChart3 size={14} className="text-violet-300" /> Smart Money Composite Score
            <Pill tone="accent">Phase 2</Pill>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed max-w-2xl">
            Ranks stocks 0-100 on institutional accumulation: delivery % z-score (25pt) + relative volume (15) + up/down volume ratio (20) + bulk/block deals (15) + delivery trend (15) + price strength (10).
            Auto-runs daily at 18:35 IST after data ingestion.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={refresh} disabled={loading} className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50" data-testid="smartflow-score-refresh">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
          <button onClick={triggerScoring} disabled={running || inProgress} className="rounded-md bg-violet-600 hover:bg-violet-500 px-3 py-1.5 text-xs text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="smartflow-score-run">
            {running || inProgress ? <Loader2 className="animate-spin" size={12} /> : <Play size={12} />}
            {inProgress ? 'Scoring…' : 'Run Scoring'}
          </button>
        </div>
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 flex-wrap text-[11px] text-zinc-400 border-t border-violet-700/20 pt-3">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={eligibleOnly} onChange={(e) => setEligibleOnly(e.target.checked)} data-testid="smartflow-eligible-only" />
          Eligible only
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={nifty200Only} onChange={(e) => setNifty200Only(e.target.checked)} data-testid="smartflow-nifty200-only" />
          Nifty 200 only
        </label>
        <label className="flex items-center gap-1.5">
          Min score
          <input
            type="number"
            min={0} max={100} step={5}
            value={minScore}
            onChange={(e) => setMinScore(Math.max(0, Math.min(100, Number(e.target.value))))}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-200 w-16 font-mono"
            data-testid="smartflow-min-score"
          />
        </label>
        <label className="flex items-center gap-1.5">
          Liquidity
          <select
            value={liquidityTier}
            onChange={(e) => setLiquidityTier(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-zinc-200"
            data-testid="smartflow-liquidity-tier"
          >
            <option value="all">All</option>
            <option value="large">Large (≥₹100Cr turnover)</option>
            <option value="mid">Mid (₹10-100Cr)</option>
            <option value="small">Small (&lt;₹10Cr)</option>
          </select>
        </label>
      </div>

      {lastRun && (
        <div className="text-[11px] text-zinc-400 flex items-center gap-3 flex-wrap">
          <Pill tone="info">{lastRun.run_id}</Pill>
          <span>date: <span className="text-zinc-200 font-mono">{lastRun.trade_date}</span></span>
          <span>scored: <span className="text-zinc-200">{lastRun.scored}</span></span>
          <span>eligible: <span className="text-emerald-300">{lastRun.eligible}</span></span>
          <span>universe: {lastRun.universe_size}</span>
          <span>finished: <span className="text-zinc-300">{lastRun.finished_at}</span></span>
        </div>
      )}

      {!lastRun && !loading && (
        <div className="rounded-lg border border-dashed border-zinc-700 px-4 py-6 text-center text-sm text-zinc-400">
          No scoring runs yet. Click <span className="text-violet-300 font-semibold">Run Scoring</span> to score the latest persisted date (~2-4 min).
        </div>
      )}

      {candidates.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-[11px]">
            <thead className="bg-zinc-900/80 text-zinc-400 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 w-6"></th>
                <th className="px-3 py-2 text-left">Symbol</th>
                <th className="px-3 py-2 text-left">Score</th>
                <th className="px-3 py-2">Eligible</th>
                <th className="px-3 py-2 text-right">Close</th>
                <th className="px-3 py-2 text-right" title="Delivery % z-score (today vs trailing 90d)">Deliv-Z</th>
                <th className="px-3 py-2 text-right" title="Today's volume vs 30d avg">Rel Vol</th>
                <th className="px-3 py-2 text-right" title="20-day sum(up day vol) / sum(down day vol)">UD Ratio</th>
                <th className="px-3 py-2 text-center" title="Bulk + Block deals in last 30 days">Deals</th>
                <th className="px-3 py-2 text-right" title="Delivery % 5-day slope">5d Trend</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => {
                const isOpen = openSym === c.symbol;
                const f = c.features || {};
                return (
                  <React.Fragment key={c.symbol}>
                    <tr
                      onClick={() => setOpenSym(isOpen ? null : c.symbol)}
                      className={`cursor-pointer border-b border-zinc-800/60 hover:bg-zinc-900/50 ${c.eligible ? '' : 'opacity-65'}`}
                      data-testid={`smartflow-cand-${c.symbol}`}
                    >
                      <td className="px-3 py-1.5 text-zinc-500">{isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</td>
                      <td className="px-3 py-1.5 font-mono font-semibold text-zinc-100">{c.symbol}</td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className="w-12 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${c.score >= 75 ? 'bg-emerald-400' : c.score >= 60 ? 'bg-amber-400' : 'bg-zinc-500'}`}
                              style={{ width: `${Math.min(100, c.score)}%` }}
                            />
                          </div>
                          <span className="text-zinc-200 font-mono w-9">{c.score?.toFixed(0)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        {c.eligible ? <Pill tone="success">✓</Pill> : <Pill tone="neutral">✗</Pill>}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{f.close?.toFixed(2)}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${f.delivery_pct_z >= 2 ? 'text-emerald-300 font-semibold' : f.delivery_pct_z >= 1 ? 'text-amber-300' : 'text-zinc-400'}`}>
                        {f.delivery_pct_z?.toFixed(2)}
                      </td>
                      <td className={`px-3 py-1.5 text-right font-mono ${f.rel_volume >= 2 ? 'text-emerald-300' : f.rel_volume >= 1.2 ? 'text-amber-300' : 'text-zinc-400'}`}>
                        {f.rel_volume?.toFixed(2)}×
                      </td>
                      <td className={`px-3 py-1.5 text-right font-mono ${f.up_down_vol_ratio_20d >= 1.5 ? 'text-emerald-300' : f.up_down_vol_ratio_20d >= 1 ? 'text-amber-300' : 'text-zinc-400'}`}>
                        {f.up_down_vol_ratio_20d?.toFixed(2)}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        {(f.bulk_count_30d || 0) + (f.block_count_30d || 0) > 0 ? (
                          <Pill tone="accent">{(f.bulk_count_30d || 0) + (f.block_count_30d || 0)}</Pill>
                        ) : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className={`px-3 py-1.5 text-right font-mono ${f.delivery_trend_5d_slope > 0 ? 'text-emerald-300' : 'text-zinc-400'}`}>
                        {f.delivery_trend_5d_slope?.toFixed(2)}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-zinc-950/60">
                        <td colSpan={10} className="px-6 py-3 text-[11px]">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-zinc-300">
                            <div>
                              <div className="text-zinc-500 mb-1 uppercase text-[10px]">Score breakdown</div>
                              <div className="font-mono">
                                {Object.entries(c.score_breakdown || {}).map(([k, v]) => (
                                  <div key={k}>{k}: <span className="text-zinc-100">{v}</span></div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className="text-zinc-500 mb-1 uppercase text-[10px]">Delivery</div>
                              <div className="font-mono">
                                today: {f.delivery_pct_today?.toFixed(2)}%<br />
                                90d avg: {f.delivery_pct_avg_90d?.toFixed(2)}%<br />
                                90d std: {f.delivery_pct_std_90d?.toFixed(2)}<br />
                                z-score: <span className={f.delivery_pct_z >= 2 ? 'text-emerald-300' : ''}>{f.delivery_pct_z?.toFixed(2)}</span>
                              </div>
                            </div>
                            <div>
                              <div className="text-zinc-500 mb-1 uppercase text-[10px]">Volume</div>
                              <div className="font-mono">
                                rel_vol: {f.rel_volume?.toFixed(2)}×<br />
                                UD ratio: {f.up_down_vol_ratio_20d?.toFixed(2)}<br />
                                bulk_30d: {f.bulk_count_30d}<br />
                                block_30d: {f.block_count_30d}<br />
                                bb_qty: {f.bulk_block_qty_30d?.toLocaleString('en-IN')}
                              </div>
                            </div>
                            <div>
                              <div className="text-zinc-500 mb-1 uppercase text-[10px]">Trend / Eligibility</div>
                              <div className="font-mono">
                                EMA20: {f.close_above_ema20 ? <span className="text-emerald-300">above ✓</span> : <span className="text-rose-300">below ✗</span>}<br />
                                EMA50: {f.close_above_ema50 ? <span className="text-emerald-300">above ✓</span> : <span className="text-rose-300">below ✗</span>}<br />
                                history: {f.history_days}d<br />
                                {!c.eligible && (
                                  <span className="text-rose-400">fails: {(c.fail_reasons || []).join(', ')}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// HERO — current status + 1-click sync today
// =====================================================================

const Hero = ({ status, onSyncToday, onManualRefresh, syncing }) => {
  const lastDate = status?.latest_date;
  const totalSymbols = status?.symbols_on_latest_date;
  const totalDays = status?.dates_persisted ?? 0;
  const isFresh = lastDate && lastDate === daysAgoISO(0); // today
  const isYesterday = lastDate && lastDate === daysAgoISO(1);

  const tone =
    status?.backfill_in_progress || status?.ingest_in_progress ? 'info'
    : isFresh || isYesterday ? 'success'
    : totalDays > 0 ? 'warn'
    : 'danger';

  const headlineText =
    status?.backfill_in_progress ? 'Backfill in progress…'
    : status?.ingest_in_progress ? 'Today\'s ingest in progress…'
    : isFresh ? `Up to date — last fetch was today (${lastDate})`
    : isYesterday ? `Up to date — last fetch was yesterday (${lastDate})`
    : totalDays > 0 ? `Last fetch: ${lastDate} — may be stale`
    : 'No data yet — run a backfill to populate history';

  const HeadIcon =
    status?.backfill_in_progress || status?.ingest_in_progress ? Loader2
    : tone === 'success' ? CheckCircle2
    : tone === 'warn' ? Clock
    : AlertCircle;

  const toneClasses = {
    success: 'border-emerald-700/40 bg-emerald-950/20 text-emerald-200',
    info: 'border-sky-700/40 bg-sky-950/20 text-sky-200',
    warn: 'border-amber-700/40 bg-amber-950/20 text-amber-200',
    danger: 'border-rose-700/40 bg-rose-950/20 text-rose-200',
  }[tone];

  return (
    <div className={`rounded-xl border ${toneClasses} p-4 space-y-3`} data-testid="smartflow-hero">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <HeadIcon size={18} className={`mt-0.5 shrink-0 ${status?.backfill_in_progress || status?.ingest_in_progress ? 'animate-spin' : ''}`} />
          <div className="min-w-0">
            <div className="text-sm font-semibold" data-testid="smartflow-hero-headline">{headlineText}</div>
            <div className="text-[11px] text-zinc-400 mt-0.5">
              {totalDays} days persisted ·
              {totalSymbols ? ` ${totalSymbols.toLocaleString('en-IN')} stocks on ${lastDate}` : ' '}
              {status?.earliest_date && ` · earliest ${status.earliest_date}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onManualRefresh}
            className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1"
            data-testid="smartflow-refresh-status"
            title="Refresh status only"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            onClick={onSyncToday}
            disabled={syncing || status?.ingest_in_progress}
            className="rounded-md bg-violet-600 hover:bg-violet-500 px-3 py-1.5 text-xs text-white inline-flex items-center gap-1.5 disabled:opacity-50 font-semibold"
            data-testid="smartflow-sync-today"
          >
            {syncing || status?.ingest_in_progress ? <Loader2 className="animate-spin" size={12} /> : <Play size={12} />}
            Sync Today Now
          </button>
        </div>
      </div>
      <div className="text-[11px] text-zinc-500 border-t border-current/20 pt-2 flex items-center gap-1.5">
        <Clock size={11} className="text-violet-400" />
        Auto-syncs every weekday at <span className="text-zinc-300">18:30 IST</span> after NSE publishes the bhav copy. You don't need to do anything daily.
      </div>
    </div>
  );
};

// =====================================================================
// BACKFILL — live progress + custom range
// =====================================================================

const BackfillCard = ({ getAuth, status, onJobStart, currentRun }) => {
  const [start, setStart] = useState(daysAgoISO(7));
  const [end, setEnd] = useState(daysAgoISO(1));
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (start > end) {
      toast.error('Start date must be ≤ end date.');
      return;
    }
    try {
      setSubmitting(true);
      const r = await axios.post(`${API}/api/admin/smartflow/data/backfill`,
        { start, end }, { headers: getAuth() });
      toast.success(r.data?.message || 'Backfill started.');
      onJobStart?.();
    } catch (e) {
      toast.error(`Backfill failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const presets = [
    { label: '7 days', s: daysAgoISO(7), e: daysAgoISO(1) },
    { label: '30 days', s: daysAgoISO(30), e: daysAgoISO(1) },
    { label: '90 days', s: daysAgoISO(90), e: daysAgoISO(1) },
    { label: '6 months', s: daysAgoISO(180), e: daysAgoISO(1) },
    { label: '1 year', s: daysAgoISO(365), e: daysAgoISO(1) },
    { label: '2 years', s: daysAgoISO(730), e: daysAgoISO(1) },
  ];

  const inProgress = status?.backfill_in_progress;
  const progress = currentRun;  // shape: {days_processed, days_ok, total_days, rows_total}

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4 space-y-3" data-testid="smartflow-backfill-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
          <Calendar size={14} className="text-violet-300" /> Backfill historical data
        </div>
        <span className="text-[11px] text-zinc-500">
          Pulls NSE bhav copy + bulk/block deals · ~0.7s per trading day · idempotent (safe to re-run)
        </span>
      </div>

      {inProgress && (
        <div className="rounded-md border border-sky-700/40 bg-sky-950/30 p-3 space-y-2" data-testid="smartflow-backfill-progress">
          <div className="flex items-center gap-2 text-sky-200 text-sm">
            <Loader2 className="animate-spin" size={14} />
            <span className="font-semibold">Backfill running…</span>
            {progress && (
              <span className="text-sky-300 font-mono">
                {progress.days_processed ?? 0}/{progress.total_days ?? '?'} days
                {progress.rows_total != null && ` · ${progress.rows_total.toLocaleString('en-IN')} rows`}
              </span>
            )}
          </div>
          {progress?.total_days && (
            <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-sky-400 transition-all"
                style={{ width: `${Math.min(100, ((progress.days_processed || 0) / progress.total_days) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        <span className="text-[11px] text-zinc-500 self-center mr-1">Quick range:</span>
        {presets.map(p => (
          <button
            key={p.label}
            onClick={() => { setStart(p.s); setEnd(p.e); }}
            className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2 py-1 text-[11px] text-zinc-300"
            data-testid={`smartflow-preset-${p.label.replace(/\s/g, '-').toLowerCase()}`}
          >
            {p.label}
          </button>
        ))}
        <span className="text-[11px] text-zinc-500 self-center ml-2">or pick custom dates →</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 items-end">
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          From
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs"
            data-testid="smartflow-backfill-start"
          />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          To
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs"
            data-testid="smartflow-backfill-end"
          />
        </label>
        <button
          onClick={submit}
          disabled={submitting || inProgress}
          className="rounded-md bg-violet-600 hover:bg-violet-500 px-3 py-2 text-xs text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50 font-semibold"
          data-testid="smartflow-backfill-run"
        >
          {submitting || inProgress ? <Loader2 className="animate-spin" size={12} /> : <Play size={12} />}
          {inProgress ? 'Running…' : 'Start Backfill'}
        </button>
      </div>
    </div>
  );
};

// =====================================================================
// INSPECTOR — tabbed (By Date / By Symbol)
// =====================================================================

const Inspector = ({ getAuth }) => {
  const [tab, setTab] = useState('by_date');
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/40" data-testid="smartflow-inspector">
      <div className="flex items-center gap-1 border-b border-zinc-800 px-2 py-2">
        <span className="text-sm font-semibold text-zinc-100 px-2">Inspect</span>
        <button
          onClick={() => setTab('by_date')}
          className={`px-3 py-1 text-xs rounded ${tab === 'by_date' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'}`}
          data-testid="smartflow-inspector-tab-bydate"
        >
          <BarChart3 size={11} className="inline mr-1 -mt-0.5" /> By Date
        </button>
        <button
          onClick={() => setTab('by_sym')}
          className={`px-3 py-1 text-xs rounded ${tab === 'by_sym' ? 'bg-zinc-800 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'}`}
          data-testid="smartflow-inspector-tab-bysym"
        >
          <TrendingUp size={11} className="inline mr-1 -mt-0.5" /> By Symbol
        </button>
      </div>
      <div className="p-4">
        {tab === 'by_date' && <ByDateView getAuth={getAuth} />}
        {tab === 'by_sym' && <BySymbolView getAuth={getAuth} />}
      </div>
    </div>
  );
};

const ByDateView = ({ getAuth }) => {
  const [d, setD] = useState(daysAgoISO(1));
  const [sortBy, setSortBy] = useState('delivery_pct');
  const [realStocksOnly, setRealStocksOnly] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const search = useCallback(async () => {
    try {
      setLoading(true);
      setHasSearched(true);
      const r = await axios.get(`${API}/api/admin/smartflow/data/by-date`, {
        headers: getAuth(),
        params: { trade_date: d, limit: 50, sort_by: sortBy, real_stocks_only: realStocksOnly },
      });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(`Search: ${e?.response?.data?.detail || e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [d, sortBy, realStocksOnly, getAuth]);

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Date
          <input
            type="date"
            value={d}
            onChange={(e) => setD(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs"
            data-testid="smartflow-bydate-date"
          />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Sort by
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs"
            data-testid="smartflow-bydate-sort"
          >
            <option value="delivery_pct">Delivery % (highest first)</option>
            <option value="total_traded_value">Turnover</option>
            <option value="delivery_qty">Delivery Qty</option>
            <option value="no_of_trades"># Trades</option>
          </select>
        </label>
        <label className="text-xs text-zinc-400 inline-flex items-center gap-1.5 mb-1.5">
          <input
            type="checkbox"
            checked={realStocksOnly}
            onChange={(e) => setRealStocksOnly(e.target.checked)}
            data-testid="smartflow-bydate-real-only"
          />
          <span>
            Real stocks only
            <span className="block text-[10px] text-zinc-500">excludes ETFs &amp; liquid funds</span>
          </span>
        </label>
        <button
          onClick={search}
          disabled={loading}
          className="rounded-md bg-emerald-600 hover:bg-emerald-500 px-3 py-2 text-xs text-white inline-flex items-center gap-1 disabled:opacity-50 font-semibold"
          data-testid="smartflow-bydate-search"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          Search
        </button>
      </div>

      {hasSearched && (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-[11px]">
            <thead className="bg-zinc-900/80 text-zinc-400 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Symbol</th>
                <th className="px-3 py-2 text-right">Close</th>
                <th className="px-3 py-2 text-right">Δ %</th>
                <th className="px-3 py-2 text-right">Volume</th>
                <th className="px-3 py-2 text-right">Delivery %</th>
                <th className="px-3 py-2 text-right">Delivery Qty</th>
                <th className="px-3 py-2 text-right">Turnover</th>
                <th className="px-3 py-2 text-center">Bulk</th>
                <th className="px-3 py-2 text-center">Block</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-zinc-500" data-testid="smartflow-bydate-empty">
                  No rows for {d}{realStocksOnly ? ' with active filters' : ''}.
                  Try a different date or run a backfill if this date isn't yet ingested.
                </td></tr>
              )}
              {rows.map((r) => {
                const change = r.prev_close > 0 ? ((r.close - r.prev_close) / r.prev_close) * 100 : 0;
                return (
                  <tr key={r.symbol} className="border-b border-zinc-800/60" data-testid={`smartflow-bydate-row-${r.symbol}`}>
                    <td className="px-3 py-1.5 font-mono font-semibold text-zinc-100">{r.symbol}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-200">{fmtNum(r.close)}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${change >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {change >= 0 ? '+' : ''}{change.toFixed(2)}%
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
                      {(r.total_traded_qty / 1000).toLocaleString('en-IN', { maximumFractionDigits: 0 })}K
                    </td>
                    <td className={`px-3 py-1.5 text-right font-mono font-semibold ${r.delivery_pct >= 70 ? 'text-emerald-300' : r.delivery_pct >= 50 ? 'text-amber-300' : 'text-zinc-400'}`}>
                      {fmtNum(r.delivery_pct)}%
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
                      {(r.delivery_qty / 1000).toLocaleString('en-IN', { maximumFractionDigits: 0 })}K
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{formatINR(r.total_traded_value)}</td>
                    <td className="px-3 py-1.5 text-center">
                      {r.bulk_deals?.length > 0 ? <Pill tone="accent">{r.bulk_deals.length}</Pill> : <span className="text-zinc-700">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {r.block_deals?.length > 0 ? <Pill tone="info">{r.block_deals.length}</Pill> : <span className="text-zinc-700">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!hasSearched && (
        <div className="rounded-lg border border-dashed border-zinc-800 p-6 text-center text-xs text-zinc-500" data-testid="smartflow-bydate-prompt">
          Pick a date and click <span className="text-emerald-300 font-semibold">Search</span> to see top stocks ranked by delivery %.
        </div>
      )}
    </div>
  );
};

const BySymbolView = ({ getAuth }) => {
  const [sym, setSym] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!sym.trim()) {
      toast.error('Enter a symbol like BHEL, RELIANCE, TCS.');
      return;
    }
    try {
      setLoading(true);
      const r = await axios.get(
        `${API}/api/admin/smartflow/data/by-symbol/${sym.trim().toUpperCase()}`,
        { headers: getAuth(), params: { lookback_days: 90 } }
      );
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error(`No data for ${sym.toUpperCase()} — backfill may not cover this symbol.`);
        setData(null);
      } else {
        toast.error(`Lookup: ${e?.response?.data?.detail || e.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <label className="text-xs text-zinc-400 flex flex-col gap-1 flex-1 max-w-xs">
          Symbol
          <input
            type="text"
            placeholder="e.g. BHEL, RELIANCE, TCS"
            value={sym}
            onChange={(e) => setSym(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs font-mono"
            data-testid="smartflow-bysym-input"
          />
        </label>
        <button
          onClick={search}
          disabled={loading}
          className="rounded-md bg-emerald-600 hover:bg-emerald-500 px-3 py-2 text-xs text-white inline-flex items-center gap-1 disabled:opacity-50 font-semibold"
          data-testid="smartflow-bysym-search"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
          Search
        </button>
      </div>

      {data?.summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <SimpleStat label="Symbol" value={data.summary.symbol} testId="smartflow-bysym-stat-sym" />
            <SimpleStat label="Days of data" value={data.summary.rows} testId="smartflow-bysym-stat-rows" />
            <SimpleStat
              label="Delivery % today"
              value={`${fmtNum(data.summary.delivery_pct_today, 2)}%`}
              tone={data.summary.delivery_pct_today >= 60 ? 'success' : data.summary.delivery_pct_today >= 40 ? 'warn' : 'neutral'}
              testId="smartflow-bysym-stat-deltoday"
            />
            <SimpleStat label="Delivery % avg 90d" value={`${fmtNum(data.summary.delivery_pct_avg_90d, 2)}%`} testId="smartflow-bysym-stat-delavg" />
            <SimpleStat
              label="Bulk + Block (30d)"
              value={(data.summary.bulk_deals_30d || 0) + (data.summary.block_deals_30d || 0)}
              tone={(data.summary.bulk_deals_30d + data.summary.block_deals_30d) > 0 ? 'accent' : 'neutral'}
              testId="smartflow-bysym-stat-deals"
            />
          </div>

          <div className="overflow-x-auto rounded-lg border border-zinc-800 max-h-[28rem]">
            <table className="w-full text-[11px]">
              <thead className="bg-zinc-900/80 text-zinc-400 uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Close</th>
                  <th className="px-3 py-2 text-right">Δ %</th>
                  <th className="px-3 py-2 text-right">Volume</th>
                  <th className="px-3 py-2 text-right">Delivery %</th>
                  <th className="px-3 py-2 text-center">Bulk · Block</th>
                </tr>
              </thead>
              <tbody>
                {[...(data.rows || [])].reverse().map((r) => {
                  const change = r.prev_close > 0 ? ((r.close - r.prev_close) / r.prev_close) * 100 : 0;
                  return (
                    <tr key={r.trade_date} className="border-b border-zinc-800/60" data-testid={`smartflow-bysym-row-${r.trade_date}`}>
                      <td className="px-3 py-1.5 font-mono text-zinc-300">{r.trade_date}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-zinc-200">{fmtNum(r.close)}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${change >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                        {change >= 0 ? '+' : ''}{change.toFixed(2)}%
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
                        {(r.total_traded_qty / 1000).toLocaleString('en-IN', { maximumFractionDigits: 0 })}K
                      </td>
                      <td className={`px-3 py-1.5 text-right font-mono font-semibold ${r.delivery_pct >= 60 ? 'text-emerald-300' : r.delivery_pct >= 40 ? 'text-amber-300' : 'text-zinc-400'}`}>
                        {fmtNum(r.delivery_pct)}%
                      </td>
                      <td className="px-3 py-1.5 text-center text-[10px] text-zinc-500">
                        {(r.bulk_deals?.length > 0 || r.block_deals?.length > 0) ? (
                          <span>
                            {r.bulk_deals?.length > 0 && <Pill tone="accent">B:{r.bulk_deals.length}</Pill>}{' '}
                            {r.block_deals?.length > 0 && <Pill tone="info">Bk:{r.block_deals.length}</Pill>}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!data && (
        <div className="rounded-lg border border-dashed border-zinc-800 p-6 text-center text-xs text-zinc-500" data-testid="smartflow-bysym-prompt">
          Type a stock symbol and click <span className="text-emerald-300 font-semibold">Search</span> to see its delivery % trend, volume pattern, and recent institutional deals.
        </div>
      )}
    </div>
  );
};

const SimpleStat = ({ label, value, tone = 'neutral', testId }) => {
  const colors = {
    success: 'text-emerald-300', warn: 'text-amber-300',
    danger: 'text-rose-300', info: 'text-sky-300',
    accent: 'text-violet-300', neutral: 'text-zinc-100',
  };
  return (
    <div className="rounded-md bg-zinc-900 border border-zinc-800 px-3 py-2.5" data-testid={testId}>
      <div className="text-[10px] uppercase text-zinc-500 tracking-wider">{label}</div>
      <div className={`text-base font-semibold mt-0.5 ${colors[tone] || colors.neutral}`}>{value}</div>
    </div>
  );
};

// =====================================================================
// ROOT
// =====================================================================

const SmartFlowTab = () => {
  const getAuth = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const [status, setStatus] = useState(null);
  const [currentRun, setCurrentRun] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/smartflow/data/status`, { headers: getAuth() });
      setStatus(r.data || {});
      // If a backfill is running, also fetch the latest run row to show progress.
      if (r.data?.backfill_in_progress) {
        try {
          const runs = await axios.get(`${API}/api/admin/smartflow/data/runs?limit=1`, { headers: getAuth() });
          const last = runs.data?.runs?.[0];
          if (last?.status === 'running') setCurrentRun(last);
        } catch { /* ignore */ }
      } else {
        setCurrentRun(null);
      }
    } catch { /* not authed yet — silent */ }
  }, [getAuth]);

  // Faster polling when a job is running.
  useEffect(() => {
    refreshStatus();
    const fast = status?.backfill_in_progress || status?.ingest_in_progress;
    const id = setInterval(refreshStatus, fast ? 3000 : 15000);
    return () => clearInterval(id);
  }, [refreshStatus, status?.backfill_in_progress, status?.ingest_in_progress]);

  const syncToday = async () => {
    try {
      setSyncing(true);
      const r = await axios.post(`${API}/api/admin/smartflow/data/ingest/today`, {}, { headers: getAuth() });
      toast.success(r.data?.message || 'Today\'s sync started.');
      refreshStatus();
    } catch (e) {
      toast.error(`Sync: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-4 px-2 md:px-4 pb-12" data-testid="smartflow-tab">
      <header className="border-b border-zinc-800 pb-3">
        <h1 className="text-base font-semibold text-zinc-100 flex items-center gap-2">
          <Activity size={16} className="text-violet-300" /> SmartFlow
          <span className="text-[10px] font-normal text-zinc-500 ml-1">— Smart-money / accumulation swing engine</span>
        </h1>
        <p className="text-[11px] text-zinc-400 mt-1.5 max-w-3xl leading-relaxed">
          4-phase engine — Data ingestion · Rule-based score · Walk-forward backtest · LightGBM ML predictor.
          Pulls NSE delivery % + bulk/block deals.
          <span className="text-violet-300"> This data structurally cannot exist on TradingView</span> — they don't have access to NSE post-trade disclosures.
        </p>
      </header>

      <Hero
        status={status}
        onSyncToday={syncToday}
        onManualRefresh={refreshStatus}
        syncing={syncing}
      />

      <ScoringPanel getAuth={getAuth} />

      <MLPanel getAuth={getAuth} />

      <BacktestPanel getAuth={getAuth} />

      <BackfillCard
        getAuth={getAuth}
        status={status}
        onJobStart={refreshStatus}
        currentRun={currentRun}
      />

      <Inspector getAuth={getAuth} />
    </div>
  );
};

export default SmartFlowTab;
